import { CollectionConfig } from "@rebasepro/types";
import talentsCollection from "./talents";
import { JOB_FUNCTIONS } from "../taxonomy/job-functions";

/**
 * The weekly vacancy newsletter — who asked for it, and what they asked for.
 *
 * The board publishes all week and, until this table, the only way a candidate
 * heard about a vacancy was by coming back to look. 308 people gave us their
 * email and their areas and were written to, on average, never.
 *
 * One row per person, and the row *is* the consent: it exists because somebody
 * ticked the box on the signup form, pressed "Suscribirme" in their own panel,
 * or answered the invitation — `consent_source` says which, `consent_at` says
 * when. That is deliberately not a boolean on the candidate: a boolean can be
 * flipped by any code that touches the row, and this is the record we would
 * have to show somebody who asks why we wrote to them.
 *
 * Unsubscribing does not delete the row. It sets `status` and a date, so the
 * next import cannot quietly re-add a person who left, and so the panel can
 * still answer "did she ever subscribe" a year later.
 *
 * ── The filters, and what empty means ────────────────────────────────
 *
 * Empty is "todo", never "nada". A subscription with no countries is one that
 * wants vacancies from every country; it is what a new subscription starts as,
 * and what somebody chooses when they would move for the right job. Nothing
 * in this product ever writes a "todos" sentinel into these arrays — a filter
 * value that no vacancy carries would match nothing, silently, forever.
 *
 * `filters_edited_at` is the line between what we guessed and what they chose.
 * While it is empty the defaults derived from their questionnaire may still be
 * recomputed; once they have saved the filters themselves, nothing derived
 * overwrites them again.
 *
 * Table, constraints and RLS in drizzle/0057.
 */

/**
 * `jobs.work_model`, verbatim.
 *
 * A subscription's values are compared against a vacancy's column, so the ids
 * have to be the vacancy's ids and the labels have to be the words the board
 * already uses. There is no `any` here: the questionnaire's "cualquiera" is
 * the *empty array*, and keeping a second spelling of it would give the filter
 * two ways to say the same thing, one of which matches no vacancy.
 */
const WORK_MODEL_LABELS: Record<string, string> = {
    on_site: "Presencial",
    remote: "Remoto",
    hybrid: "Híbrido"
};

/**
 * `jobs.sustainability_focus`, verbatim, and duplicated on purpose.
 *
 * Same trade `views/_talent_taxonomy.ts` explains: the source list is nested
 * four levels deep inside a property definition in `jobs.ts`, and importing it
 * would couple this file to the shape of that config rather than to the data.
 * The cost is stated plainly so nobody has to discover it — an id that drifts
 * from `jobs.ts` is a filter that matches no vacancy and reports nothing, so
 * these two lists are edited together or not at all.
 */
const FOCUS_LABELS: Record<string, string> = {
    estrategia_esg: "Estrategia ESG/Sostenibilidad",
    informes_sostenibilidad: "Informes Sostenibilidad",
    gestion_gobernanza: "Gestión gobernanza",
    compliance: "Compliance",
    materialidad: "Materialidad",
    cambio_climatico: "Cambio climático",
    energia: "Energía",
    agua: "Agua",
    economia_circular: "Economía circular",
    biodiversidad: "Biodiversidad",
    impacto_social: "Impacto social",
    dei: "Diversidad, equidad e inclusión",
    derechos_humanos: "Derechos Humanos",
    finanzas_sostenibles: "Finanzas sostenibles",
    inversion_impacto: "Inversión de impacto",
    cadena_suministro_sostenible: "Cadena de suministro sostenible",
    otro: "Otra área"
};

const asEnum = (labels: Record<string, string>) =>
    Object.entries(labels).map(([id, label]) => ({ id, label }));

const digestSubscriptionsCollection: CollectionConfig = {
    name: "Boletín de vacantes",
    singularName: "Suscripción al boletín",
    slug: "digest-subscriptions",
    table: "digest_subscriptions",
    securityRules: [
        {
            // A candidate owns their subscription outright: they read it,
            // change the filters and unsubscribe. The ownership test goes
            // through `talents`, which is where the account lives — and which
            // is FORCE RLS, so this EXISTS is itself evaluated under the
            // caller's own policies there. Somebody who cannot see the
            // candidate row cannot claim its subscription.
            name: "talent_own",
            operation: "all",
            using: "EXISTS (SELECT 1 FROM talents t WHERE t.id = digest_subscriptions.talent_id AND (t.auth_user_id)::text = auth.uid())",
            withCheck: "EXISTS (SELECT 1 FROM talents t WHERE t.id = digest_subscriptions.talent_id AND (t.auth_user_id)::text = auth.uid())"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
        // No public rule, and none is coming. The links that work signed out —
        // unsubscribe, one-click unsubscribe, accepting an invitation — are
        // token-gated backend functions that go through `dataAsAdmin`. An
        // anonymous policy here would have to trust a column of this table as
        // a credential, which is a policy that reads every row to check one.
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        talent_id: {
            name: "Candidato",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => talentsCollection,
                localKey: "talent_id"
            },
            validation: { required: true },
            description: "La persona suscrita. Una suscripción por persona.",
            // Pinned: a subscription that changes hands is one person's consent
            // used to write to another. Move it by unsubscribing and
            // subscribing, which leaves both facts recorded.
            admin: { readOnly: true }
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "active", label: "Activa", color: "green" },
                { id: "unsubscribed", label: "Dada de baja", color: "grayLight" }
            ],
            defaultValue: "active",
            description: "Dada de baja conserva la fila: es la prueba de qué se consintió y cuándo."
        },
        countries: {
            name: "Países",
            type: "array",
            columnType: "text[]",
            // No enum: these ids *are* the Spanish country names — the same
            // strings the questionnaire offers and the vacancies store — so a
            // label list would be twenty-one lines mapping each word to itself.
            of: { type: "string", name: "País" },
            defaultValue: [],
            description: "Países de los que quiere recibir vacantes. Vacío = todos los países, que es lo que trae una suscripción nueva."
        },
        work_models: {
            name: "Modalidad",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Modalidad",
                enum: asEnum(WORK_MODEL_LABELS)
            },
            defaultValue: [],
            description: "Vacío = cualquier modalidad."
        },
        focus: {
            name: "Áreas de sostenibilidad",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Área",
                enum: asEnum(FOCUS_LABELS)
            },
            defaultValue: [],
            description: "Basta con que la vacante toque una de ellas. Vacío = todas las áreas."
        },
        // Imported, unlike the two lists above: config/taxonomy/job-functions.ts
        // is a flat module written to be shared, so there is nothing to couple
        // to but the ids themselves. drizzle/0076.
        job_functions: {
            name: "Tipo de trabajo",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Tipo",
                enum: JOB_FUNCTIONS.map(({ id, label }) => ({ id, label }))
            },
            defaultValue: [],
            description: "La vacante tiene que ser de uno de ellos. Vacío = cualquier tipo de trabajo."
        },
        filters_edited_at: {
            name: "Filtros elegidos por la persona",
            type: "date",
            description: "Cuándo los ajustó ella misma. Vacío = siguen siendo los que dedujimos de su cuestionario, y se pueden recalcular.",
            admin: { readOnly: true }
        },
        consent_at: {
            name: "Consintió el",
            type: "date",
            validation: { required: true },
            description: "Cuándo aceptó recibir el boletín.",
            admin: { readOnly: true }
        },
        consent_source: {
            name: "Cómo se suscribió",
            type: "string",
            enum: [
                { id: "registro", label: "Al registrarse", color: "blue" },
                { id: "panel", label: "Desde su panel", color: "green" },
                { id: "invitacion", label: "Por invitación", color: "purple" }
            ],
            validation: { required: true },
            admin: { readOnly: true }
        },
        unsubscribed_at: {
            name: "Se dio de baja el",
            type: "date",
            admin: { readOnly: true }
        },
        /**
         * Declared, because a column the collection does not know is a column
         * the data plane refuses to write — and the signup form has to write
         * this one. Out of the form and out of the table, because it is the
         * entire authorisation of the unsubscribe link: printing it in a list
         * puts a working "darse de baja por ella" button on screen.
         */
        unsubscribe_token: {
            name: "Token de baja",
            type: "string",
            description: "La credencial del enlace de baja. Se renueva cada vez que la persona se vuelve a suscribir.",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        last_sent_at: {
            name: "Último boletín enviado",
            type: "date",
            description: "Sólo se escribe cuando el servidor de correo aceptó el envío. Es también desde dónde empieza a mirar el siguiente.",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Creada",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizada",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Personas",
        icon: "Send",
        sort: ["created_at", "desc"],
        // Short on purpose: the list renders these as columns without headers,
        // so three arrays side by side would read as one wall of chips. What
        // the person actually chose is the subtitle below, in words.
        listProperties: ["talent_id", "status", "consent_source", "last_sent_at"],
        display: {
            title: "talent_id",
            /**
             * The filters as a sentence, because that is the only question this
             * list is ever asked: "what does she get?".
             *
             * Every value is on the row already, so this costs no request. An
             * empty array prints nothing rather than "0 países" — empty means
             * "todos" here, and a count would say the opposite of the truth.
             */
            subtitle: ({ entity }) => {
                const v = entity.values as {
                    countries?: string[] | null;
                    work_models?: string[] | null;
                    focus?: string[] | null;
                };
                const parts: string[] = [];
                if (v.countries?.length) parts.push(v.countries.join(", "));
                if (v.work_models?.length) {
                    parts.push(v.work_models.map(id => WORK_MODEL_LABELS[id] ?? id).join(", "));
                }
                if (v.focus?.length) {
                    parts.push(v.focus.map(id => FOCUS_LABELS[id] ?? id).join(", "));
                }
                return parts.length ? parts.join(" · ") : "Todas las vacantes";
            },
            status: "status",
            date: "last_sent_at"
        },
        filterPresets: [
            {
                label: "Activas",
                filterValues: { status: ["==", "active"] },
                sort: ["created_at", "desc"]
            },
            {
                // The people who left, which is the half worth reading: a rise
                // here is the newsletter telling you something about itself.
                label: "Dadas de baja",
                filterValues: { status: ["==", "unsubscribed"] },
                sort: ["unsubscribed_at", "desc"]
            }
        ],
        previewProperties: ["talent_id", "status"]
    }
};

export default digestSubscriptionsCollection;
